import React, {
  useState, useEffect, useRef, useMemo, useCallback,
} from 'react';
import {
  Marker, Popup, Source, Layer,
} from '@vis.gl/react-maplibre';
import type { MapRef } from '@vis.gl/react-maplibre';
import MapLibreMap from '../../components/maps/MapLibreMap';
import MapDotMarker from '../../components/maps/MapDotMarker';
import './ShiftHistoryPage.css';
import {
  createCircleFeature,
  createFeatureCollection,
  createLineFeature,
  getBoundsFromPositions,
} from '../../utils/geo';
import { getAvatarAccentStyle } from '../../utils/avatarAccent';
import { supabase } from '../../supabaseClient';
import { parseEwkbPoint } from '../../utils/geo';
import { getLocale, t, tn, useI18n } from '../../i18n';

interface LocationPoint {
  id: string;
  recordedAt: number;
  lat: number;
  lng: number;
  accuracy: number | null;
  speed: number | null;
  heading: number | null;
}

interface RawLocationRow {
  id: string;
  recorded_at: string;
  location: string | null;
  accuracy: number | null;
  speed: number | null;
  heading: number | null;
}

function parseLocationRow(row: RawLocationRow): LocationPoint | null {
  const coords = row.location ? parseEwkbPoint(row.location) : null;
  if (!coords) return null;
  return {
    id: row.id,
    recordedAt: new Date(row.recorded_at).getTime(),
    lat: coords.latitude,
    lng: coords.longitude,
    accuracy: row.accuracy,
    speed: row.speed,
    heading: row.heading,
  };
}

interface AttendanceSession {
  id: string;
  guard_id: string | null;
  site_id: string | null;
  planned_start_utc: string | null;
  planned_end_utc: string | null;
  check_in_at: string | null;
  check_out_at: string | null;
  status: string | null;
  minutes_worked: number | null;
  minutes_late: number | null;
  shift_assignment_id: string | null;
  profiles?: { full_name: string | null; avatar_url: string | null } | null;
  sites?: { name: string } | null;
  [key: string]: unknown;
}

interface ShiftDetail {
  session: AttendanceSession;
  breaks: unknown[];
  events: unknown[];
  patrolRuns: unknown[];
}

type MaybeSingle<T> = T | T[] | null | undefined;

interface ShiftExecutionRow {
  id: string;
  guard_id: string | null;
  site_id: string | null;
  planned_start_at: string | null;
  planned_end_at: string | null;
  actual_clock_in_at: string | null;
  actual_clock_out_at: string | null;
  status: string | null;
  clock_in_late_by_seconds: number | null;
  profiles?: MaybeSingle<{ full_name: string | null; avatar_url: string | null }>;
  sites?: MaybeSingle<{ name: string | null }>;
}

const SHIFT_EXECUTION_SESSION_SELECT = `
  id,
  guard_id,
  site_id,
  planned_start_at,
  planned_end_at,
  actual_clock_in_at,
  actual_clock_out_at,
  status,
  clock_in_late_by_seconds,
  profiles:profiles!shift_executions_guard_id_fkey(full_name, avatar_url),
  sites:sites!shift_executions_site_id_fkey(name)
`;

interface ShiftExecutionSegmentRow {
  id: string;
  patrol_id: string | null;
  planned_segment_order: number;
  planned_segment_type: string;
  planned_label: string;
  planned_is_paid: boolean | null;
  planned_start_at: string | null;
  planned_end_at: string | null;
  actual_started_at: string | null;
  actual_ended_at: string | null;
  status: string;
  patrols?: MaybeSingle<{ name: string | null; estimated_duration_minutes: number | null }>;
}

interface ShiftExecutionEventRow {
  id: string;
  occurred_at: string;
  event_type: string;
  summary: string | null;
}

interface ShiftExecutionCheckpointRow {
  id: string;
  shift_execution_segment_id: string | null;
  patrol_id: string | null;
  checkpoint_id: string | null;
  checkpoint_name: string | null;
  planned_sequence_order: number | null;
  status: string;
  recorded_at: string;
  checkpoints?: MaybeSingle<{ name: string | null; location: string | null; geofence_radius: number | null }>;
}

interface PatrolCheckpointPlanRow {
  id: string;
  patrol_id: string;
  checkpoint_id: string;
  sequence_order: number;
  is_required: boolean;
  label: string | null;
  checkpoints?: MaybeSingle<{ name: string | null; location: string | null; geofence_radius: number | null }>;
}

function getMinutesBetween(startAt: string | null | undefined, endAt: string | null | undefined): number | null {
  if (!startAt || !endAt) return null;
  const startMs = new Date(startAt).getTime();
  const endMs = new Date(endAt).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;
  return Math.round((endMs - startMs) / 60000);
}

function unwrapRelation<T>(value: MaybeSingle<T>): T | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

// Keep the replay UI stable by normalizing execution rows into the page's existing attendance-shaped model.
function mapShiftExecutionSession(row: ShiftExecutionRow): AttendanceSession {
  const profile = unwrapRelation(row.profiles);
  const site = unwrapRelation(row.sites);

  return {
    id: row.id,
    guard_id: row.guard_id,
    site_id: row.site_id,
    planned_start_utc: row.planned_start_at,
    planned_end_utc: row.planned_end_at,
    check_in_at: row.actual_clock_in_at,
    check_out_at: row.actual_clock_out_at,
    status: row.status,
    minutes_worked: getMinutesBetween(row.actual_clock_in_at, row.actual_clock_out_at),
    minutes_late: row.clock_in_late_by_seconds != null
      ? Math.round(row.clock_in_late_by_seconds / 60)
      : null,
    shift_assignment_id: null,
    profiles: profile ?? null,
    sites: site?.name ? { name: site.name } : null,
  };
}

function mapBreakLogs(segmentRows: ShiftExecutionSegmentRow[]): BreakLog[] {
  return segmentRows
    .filter((segment) => segment.planned_segment_type === 'break')
    .map((segment) => ({
      id: segment.id,
      started_at: segment.actual_started_at ?? segment.planned_start_at ?? null,
      ended_at: segment.actual_ended_at ?? null,
      duration_actual_min: getMinutesBetween(segment.actual_started_at, segment.actual_ended_at),
      break_types: {
        name: segment.planned_label,
        is_paid: segment.planned_is_paid,
      },
    }));
}

function mapExecutionEvents(eventRows: ShiftExecutionEventRow[]): AttendanceEvent[] {
  const eventTypesHandledBySegments = new Set([
    'segment_started',
    'segment_ended',
    'checkpoint_completed',
    'checkpoint_missed',
  ]);

  return eventRows
    .filter((event) => !eventTypesHandledBySegments.has(event.event_type))
    .map((event) => ({
      id: event.id,
      recorded_at: event.occurred_at,
      event_type: event.event_type,
      summary: event.summary,
    }));
}

// Rebuild patrol checkpoint state from planned patrol checkpoints plus execution completions.
function mapPatrolRuns(
  segmentRows: ShiftExecutionSegmentRow[],
  checkpointRows: ShiftExecutionCheckpointRow[],
  patrolPlans: PatrolCheckpointPlanRow[],
): PatrolRun[] {
  const plansByPatrolId = new Map<string, PatrolCheckpointPlanRow[]>();
  patrolPlans.forEach((plan) => {
    if (!plansByPatrolId.has(plan.patrol_id)) {
      plansByPatrolId.set(plan.patrol_id, []);
    }
    plansByPatrolId.get(plan.patrol_id)!.push(plan);
  });

  return segmentRows
    .filter((segment): segment is ShiftExecutionSegmentRow & { patrol_id: string } => Boolean(segment.patrol_id))
    .map((segment) => {
      const segmentCompletions = checkpointRows
        .filter((checkpoint) => checkpoint.shift_execution_segment_id === segment.id)
        .sort((left, right) => {
          const leftOrder = left.planned_sequence_order ?? Number.MAX_SAFE_INTEGER;
          const rightOrder = right.planned_sequence_order ?? Number.MAX_SAFE_INTEGER;
          if (leftOrder !== rightOrder) return leftOrder - rightOrder;
          return new Date(left.recorded_at).getTime() - new Date(right.recorded_at).getTime();
        });
      const completionByCheckpointId = new Map<string, ShiftExecutionCheckpointRow>();
      const completionBySequence = new Map<number, ShiftExecutionCheckpointRow>();

      segmentCompletions.forEach((checkpoint) => {
        if (checkpoint.checkpoint_id && !completionByCheckpointId.has(checkpoint.checkpoint_id)) {
          completionByCheckpointId.set(checkpoint.checkpoint_id, checkpoint);
        }
        if (checkpoint.planned_sequence_order != null && !completionBySequence.has(checkpoint.planned_sequence_order)) {
          completionBySequence.set(checkpoint.planned_sequence_order, checkpoint);
        }
      });

      const usedCompletionIds = new Set<string>();
      const plannedCheckpoints = (plansByPatrolId.get(segment.patrol_id) ?? [])
        .slice()
        .sort((left, right) => left.sequence_order - right.sequence_order)
        .map((plan) => {
          const completion = completionByCheckpointId.get(plan.checkpoint_id)
            ?? completionBySequence.get(plan.sequence_order);

          if (completion) {
            usedCompletionIds.add(completion.id);
          }

          const checkpointRecord = unwrapRelation(completion?.checkpoints) ?? unwrapRelation(plan.checkpoints);
          const coords = checkpointRecord?.location ? parseEwkbPoint(checkpointRecord.location) : null;

          return {
            id: completion?.id ?? `${segment.id}_${plan.id}`,
            sequence_order: plan.sequence_order,
            status: completion?.status ?? 'pending',
            is_required: plan.is_required,
            label: plan.label ?? completion?.checkpoint_name ?? null,
            arrived_at: completion?.recorded_at ?? null,
            completed_at: completion?.recorded_at ?? null,
            checkpoints: checkpointRecord
              ? {
                name: checkpointRecord.name ?? completion?.checkpoint_name ?? null,
                latitude: coords?.latitude ?? null,
                longitude: coords?.longitude ?? null,
                geofence_radius: checkpointRecord.geofence_radius ?? null,
              }
              : null,
          };
        });

      const additionalCheckpoints = segmentCompletions
        .filter((completion) => !usedCompletionIds.has(completion.id))
        .map((completion, index) => {
          const checkpointRecord = unwrapRelation(completion.checkpoints);
          const coords = checkpointRecord?.location
            ? parseEwkbPoint(checkpointRecord.location)
            : null;

          return {
            id: completion.id,
            sequence_order: completion.planned_sequence_order ?? (plannedCheckpoints.length + index + 1),
            status: completion.status,
            is_required: true,
            label: completion.checkpoint_name,
            arrived_at: completion.recorded_at,
            completed_at: completion.recorded_at,
            checkpoints: checkpointRecord
              ? {
                name: checkpointRecord.name ?? completion.checkpoint_name,
                latitude: coords?.latitude ?? null,
                longitude: coords?.longitude ?? null,
                geofence_radius: checkpointRecord.geofence_radius ?? null,
              }
              : null,
          };
        });

      return {
        id: segment.id,
        patrol_id: segment.patrol_id,
        started: segment.actual_started_at ?? segment.planned_start_at ?? null,
        completed: segment.actual_ended_at ?? (segment.status === 'completed' ? segment.planned_end_at : null),
        patrols: unwrapRelation(segment.patrols),
        patrol_run_checkpoints: [...plannedCheckpoints, ...additionalCheckpoints]
          .sort((left, right) => left.sequence_order - right.sequence_order),
      };
    });
}

async function fetchCompanyId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('profiles')
    .select('company_id')
    .eq('id', user.id)
    .single();
  return (data as { company_id: string } | null)?.company_id ?? null;
}

async function fetchCompletedShifts(companyId: string): Promise<AttendanceSession[]> {
  const { data, error } = await supabase
    .from('shift_executions')
    .select(SHIFT_EXECUTION_SESSION_SELECT)
    .eq('company_id', companyId)
    .not('actual_clock_in_at', 'is', null)
    .order('actual_clock_in_at', { ascending: false })
    .limit(200);

  if (error) throw error;
  return ((data ?? []) as ShiftExecutionRow[]).map(mapShiftExecutionSession);
}

async function fetchShiftSessionById(shiftExecutionId: string): Promise<AttendanceSession> {
  const { data, error } = await supabase
    .from('shift_executions')
    .select(SHIFT_EXECUTION_SESSION_SELECT)
    .eq('id', shiftExecutionId)
    .single();

  if (error) throw error;
  return mapShiftExecutionSession(data as ShiftExecutionRow);
}

async function fetchShiftDetail(shiftExecutionId: string): Promise<ShiftDetail> {
  const { data: session, error: sessionErr } = await supabase
    .from('shift_executions')
    .select(SHIFT_EXECUTION_SESSION_SELECT)
    .eq('id', shiftExecutionId)
    .single();

  if (sessionErr) throw sessionErr;

  const [segmentsRes, eventsRes, checkpointRes] = await Promise.all([
    supabase
      .from('shift_execution_segments')
      .select(`
        id,
        patrol_id,
        planned_segment_order,
        planned_segment_type,
        planned_label,
        planned_is_paid,
        planned_start_at,
        planned_end_at,
        actual_started_at,
        actual_ended_at,
        status,
        patrols:patrols!shift_execution_segments_patrol_id_fkey(name, estimated_duration_minutes)
      `)
      .eq('shift_execution_id', shiftExecutionId)
      .order('planned_segment_order'),

    supabase
      .from('shift_execution_events')
      .select('id, occurred_at, event_type, summary')
      .eq('shift_execution_id', shiftExecutionId)
      .order('occurred_at'),

    supabase
      .from('shift_execution_checkpoint_completions')
      .select(`
        id,
        shift_execution_segment_id,
        patrol_id,
        checkpoint_id,
        checkpoint_name,
        planned_sequence_order,
        status,
        recorded_at,
        checkpoints:checkpoints!shift_execution_checkpoint_completions_checkpoint_id_fkey(name, location, geofence_radius)
      `)
      .eq('shift_execution_id', shiftExecutionId)
      .order('recorded_at'),
  ]);

  if (segmentsRes.error) throw segmentsRes.error;
  if (eventsRes.error) throw eventsRes.error;
  if (checkpointRes.error) throw checkpointRes.error;

  const segmentRows = (segmentsRes.data ?? []) as ShiftExecutionSegmentRow[];
  const checkpointRows = (checkpointRes.data ?? []) as ShiftExecutionCheckpointRow[];
  const patrolIds = [...new Set(
    segmentRows
      .map((segment) => segment.patrol_id)
      .filter((patrolId): patrolId is string => Boolean(patrolId)),
  )];

  let patrolCheckpointPlans: PatrolCheckpointPlanRow[] = [];
  if (patrolIds.length) {
    const { data: patrolPlanData, error: patrolPlanError } = await supabase
      .from('patrol_checkpoints')
      .select(`
        id,
        patrol_id,
        checkpoint_id,
        sequence_order,
        is_required,
        label,
        checkpoints:checkpoints!patrol_checkpoints_checkpoint_id_fkey(name, location, geofence_radius)
      `)
      .in('patrol_id', patrolIds)
      .order('sequence_order');

    if (patrolPlanError) throw patrolPlanError;
    patrolCheckpointPlans = (patrolPlanData ?? []) as PatrolCheckpointPlanRow[];
  }

  return {
    session: mapShiftExecutionSession(session as ShiftExecutionRow),
    breaks: mapBreakLogs(segmentRows),
    events: mapExecutionEvents((eventsRes.data ?? []) as ShiftExecutionEventRow[]),
    patrolRuns: mapPatrolRuns(segmentRows, checkpointRows, patrolCheckpointPlans),
  };
}

async function fetchShiftLocationHistory(
  shiftExecutionId: string,
  guardId?: string | null,
  startTime?: string | null,
  endTime?: string | null,
): Promise<LocationPoint[]> {
  // Prefer execution-scoped telemetry, but fall back to guard history so older replays still render.
  const { data, error } = await supabase
    .from('shift_execution_segment_locations')
    .select('id, recorded_at, location, accuracy:accuracy_m, speed, heading')
    .eq('shift_execution_id', shiftExecutionId)
    .order('recorded_at');

  if (error) throw error;

  const executionLocations = ((data ?? []) as RawLocationRow[]).map(parseLocationRow).filter((point): point is LocationPoint => point !== null);
  if (executionLocations.length || !guardId || !startTime) {
    return executionLocations;
  }

  const { data: fallbackData, error: fallbackError } = await supabase
    .from('guard_location_history')
    .select('id, recorded_at, location, accuracy, speed, heading')
    .eq('guard_id', guardId)
    .gte('recorded_at', startTime)
    .lte('recorded_at', endTime ?? new Date().toISOString())
    .order('recorded_at');

  if (fallbackError) throw fallbackError;
  return ((fallbackData ?? []) as RawLocationRow[]).map(parseLocationRow).filter((point): point is LocationPoint => point !== null);
}

async function fetchPatrolRunLocations(patrolRunIds: string[]): Promise<LocationPoint[]> {
  if (!patrolRunIds.length) return [];

  const { data, error } = await supabase
    .from('shift_execution_segment_locations')
    .select('id, recorded_at, location, accuracy:accuracy_m, speed, heading')
    .in('shift_execution_segment_id', patrolRunIds)
    .order('recorded_at');

  if (error) throw error;
  return ((data ?? []) as RawLocationRow[]).map(parseLocationRow).filter((p): p is LocationPoint => p !== null);
}

type FmtMode = 'datetime' | 'date' | 'time';
type ShiftSort = 'latest' | 'oldest' | 'duration_desc' | 'guard_asc' | 'site_asc';
type ShiftRange = 'all' | '7d' | '30d' | '90d';
type InspectorTab = 'overview' | 'timeline' | 'patrols';
type TimelineCategory = 'event' | 'break' | 'patrol' | 'checkpoint';
type TimelineFilter = 'all' | TimelineCategory;

interface BreakType {
  name?: string | null;
  is_paid?: boolean | null;
}

interface BreakLog {
  id: string;
  started_at?: string | null;
  ended_at?: string | null;
  duration_actual_min?: number | null;
  break_types?: BreakType | null;
}

interface AttendanceEvent {
  id: string;
  recorded_at: string;
  event_type: string;
  summary?: string | null;
}

interface RunCheckpoint {
  id: string;
  sequence_order: number;
  status: string;
  is_required?: boolean;
  label?: string | null;
  arrived_at?: string | null;
  completed_at?: string | null;
  checkpoints?: {
    name?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    geofence_radius?: number | null;
  } | null;
}

interface PatrolRun {
  id: string;
  patrol_id?: string | null;
  started?: string | null;
  completed?: string | null;
  patrols?: { name?: string | null; estimated_duration_minutes?: number | null } | null;
  patrol_run_checkpoints?: RunCheckpoint[];
}

interface TypedShiftDetail {
  session: AttendanceSession;
  breaks: BreakLog[];
  events: AttendanceEvent[];
  patrolRuns: PatrolRun[];
}

interface MappedCheckpoint {
  id: string;
  lat: number;
  lng: number;
  name?: string | null;
  status: string;
  geofence?: number | null;
  completedAt?: string | null;
  patrolName?: string | null;
}

interface TimelineItem {
  id: string;
  ts: number;
  label: string;
  color: string;
  icon: TimelineCategory;
  category: TimelineCategory;
  meta: string;
  groupId?: string;
}

interface ActivePatrol {
  patrolRun: PatrolRun;
  locations: LocationPoint[];
}

interface ReplayContext {
  mode: string;
  guardName: string;
  siteName: string;
  date: string;
  start: string;
  end: string;
  duration: string;
  status: string | null;
  patrolName: string | null;
  gpsPoints: number;
  events: number;
  patrolRuns: number;
  currentPatrolCp: number;
}

interface ShiftHistoryNavigationTarget {
  shiftExecutionId: string;
  nonce: number;
}

interface Props {
  navigationTarget?: ShiftHistoryNavigationTarget | null;
  onNavigationHandled?: () => void;
}

function mergeAttendanceSessions(primary: AttendanceSession[], secondary: AttendanceSession[]): AttendanceSession[] {
  const byId = new Map<string, AttendanceSession>();

  primary.forEach((session) => {
    byId.set(session.id, session);
  });

  secondary.forEach((session) => {
    if (!byId.has(session.id)) {
      byId.set(session.id, session);
    }
  });

  return Array.from(byId.values());
}

interface EventToastState {
  item: TimelineItem;
  key: string;
}

const STATUS_COLOR: Record<string, string> = {
  active: '#3b82f6',
  completed: '#22c55e',
  present: '#22c55e',
  late: '#f59e0b',
  partial: '#f59e0b',
  absent: '#ef4444',
  no_show: '#ef4444',
  cancelled: '#6b7280',
  scheduled: '#3b82f6',
  excused: '#8b5cf6',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Active||shift',
  completed: 'Completed||shift',
  present: 'Present||shift',
  late: 'Late||shift',
  partial: 'Partial||shift',
  absent: 'Absent||shift',
  no_show: 'No Show',
  cancelled: 'Cancelled||shift',
  scheduled: 'Scheduled||shift',
  excused: 'Excused||shift',
};

const CHECKPOINT_STATUS_COLOR: Record<string, string> = {
  completed: '#22c55e',
  skipped: '#f59e0b',
  missed: '#ef4444',
  failed: '#ef4444',
  pending: '#6b7280',
};

const SHIFT_SORT_OPTIONS: Array<{ value: ShiftSort; label: string }> = [
  { value: 'latest', label: 'Latest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'duration_desc', label: 'Longest duration' },
  { value: 'guard_asc', label: 'Guard A-Z' },
  { value: 'site_asc', label: 'Site A-Z' },
];

const SHIFT_RANGE_OPTIONS: Array<{ value: ShiftRange; label: string }> = [
  { value: 'all', label: 'All time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

const INSPECTOR_TABS: Array<{ value: InspectorTab; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'timeline', label: 'Timeline' },
  { value: 'patrols', label: 'Patrols' },
];

const TIMELINE_FILTERS: Array<{ value: TimelineFilter; label: string }> = [
  { value: 'all', label: 'All activity' },
  { value: 'event', label: 'Execution' },
  { value: 'break', label: 'Breaks' },
  { value: 'patrol', label: 'Patrols' },
  { value: 'checkpoint', label: 'Checkpoints' },
];

const SPEED_OPTIONS = [1, 5, 10, 30, 60, 120];
const TICK_MS = 100;

function fmt(ts: string | number | null | undefined, mode: FmtMode = 'datetime'): string {
  if (!ts) return '-';
  const d = new Date(ts);
  if (mode === 'date') return d.toLocaleDateString(getLocale());
  if (mode === 'time') return d.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return d.toLocaleString(getLocale(), { dateStyle: 'short', timeStyle: 'short' } as Intl.DateTimeFormatOptions);
}

function fmtDuration(minutes: number | null | undefined): string {
  if (minutes == null) return '-';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtMs(ms: number | null | undefined): string {
  if (!ms) return '-';
  const d = new Date(ms);
  return d.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function getGuardName(session: Partial<AttendanceSession> | null | undefined): string {
  return session?.profiles?.full_name || t('Unknown Guard');
}

function getSiteName(session: Partial<AttendanceSession> | null | undefined): string {
  return session?.sites?.name || '-';
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'G';
}

function isRequiredRunCheckpoint(checkpoint: RunCheckpoint): boolean {
  return checkpoint.is_required !== false;
}

function getRunCheckpointName(checkpoint: RunCheckpoint): string {
  return checkpoint.label || checkpoint.checkpoints?.name || t('Checkpoint {number}', { number: checkpoint.sequence_order });
}

function getWindowMinutes(start?: string | null, end?: string | null): number | null {
  if (!start || !end) return null;
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return null;
  return Math.round((endMs - startMs) / 60000);
}

function getShiftDurationMinutes(session: Partial<AttendanceSession> | null | undefined): number | null {
  if (!session) return null;
  if (session.minutes_worked != null) return session.minutes_worked;
  return getWindowMinutes(session.check_in_at, session.check_out_at);
}

function matchesRange(ts: string | null | undefined, range: ShiftRange): boolean {
  if (range === 'all' || !ts) return true;
  const valueMs = new Date(ts).getTime();
  if (!Number.isFinite(valueMs)) return false;
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  const cutoffMs = Date.now() - days * 24 * 60 * 60 * 1000;
  return valueMs >= cutoffMs;
}

function compareShifts(a: AttendanceSession, b: AttendanceSession, sortBy: ShiftSort): number {
  const aCheckIn = a.check_in_at ? new Date(a.check_in_at).getTime() : 0;
  const bCheckIn = b.check_in_at ? new Date(b.check_in_at).getTime() : 0;
  const aDuration = getShiftDurationMinutes(a) ?? 0;
  const bDuration = getShiftDurationMinutes(b) ?? 0;
  const aGuard = getGuardName(a);
  const bGuard = getGuardName(b);
  const aSite = getSiteName(a);
  const bSite = getSiteName(b);

  if (sortBy === 'oldest') return aCheckIn - bCheckIn;
  if (sortBy === 'duration_desc') return bDuration - aDuration || bCheckIn - aCheckIn;
  if (sortBy === 'guard_asc') return aGuard.localeCompare(bGuard, undefined, { sensitivity: 'base' }) || bCheckIn - aCheckIn;
  if (sortBy === 'site_asc') return aSite.localeCompare(bSite, undefined, { sensitivity: 'base' }) || bCheckIn - aCheckIn;
  return bCheckIn - aCheckIn;
}

function getLatestTimelineItemBefore(items: TimelineItem[], ts: number): TimelineItem | null {
  let latest: TimelineItem | null = null;
  for (let i = 0; i < items.length; i += 1) {
    if (items[i].ts <= ts) {
      latest = items[i];
    } else {
      break;
    }
  }
  return latest;
}

function buildTimelineItems(detail: TypedShiftDetail): TimelineItem[] {
  const items: TimelineItem[] = [];

  detail.events.forEach((event) => {
    const labelMap: Record<string, string> = {
      check_in: 'Clock In',
      check_out: 'Clock Out',
      auto_check_out: 'Auto Clock Out',
      incident_raised: 'Incident Raised',
      manual_intervention: 'Manual Intervention',
      site_departure_started: 'Site Departure Started',
      site_departure_returned: 'Returned To Site',
      segment_missed: 'Segment Missed',
    };

    const label = event.summary || t(labelMap[event.event_type] || event.event_type.replace(/_/g, ' '));
    const color = event.event_type === 'check_in'
      ? '#22c55e'
      : (event.event_type === 'check_out' || event.event_type === 'auto_check_out')
        ? '#ef4444'
        : event.event_type === 'incident_raised'
          ? '#f97316'
          : event.event_type === 'manual_intervention'
            ? '#8b5cf6'
            : event.event_type.startsWith('site_departure')
              ? '#f59e0b'
              : event.event_type === 'segment_missed'
                ? '#ef4444'
        : '#7c8aa5';

    items.push({
      id: event.id,
      ts: new Date(event.recorded_at).getTime(),
      label,
      color,
      icon: 'event',
      category: 'event',
      meta: t('Execution event'),
    });
  });

  detail.breaks.forEach((entry) => {
    if (entry.started_at) {
      items.push({
        id: `break_start_${entry.id}`,
        ts: new Date(entry.started_at).getTime(),
        label: `${t('Break Started')}${entry.break_types?.name ? ` - ${entry.break_types.name}` : ''}`,
        color: '#f59e0b',
        icon: 'break',
        category: 'break',
        meta: entry.break_types?.is_paid ? t('Paid break') : t('Break'),
      });
    }

    if (entry.ended_at) {
      items.push({
        id: `break_end_${entry.id}`,
        ts: new Date(entry.ended_at).getTime(),
        label: `${t('Break Ended')}${entry.duration_actual_min != null ? ` - ${entry.duration_actual_min}m` : ''}`,
        color: '#f59e0b',
        icon: 'break',
        category: 'break',
        meta: entry.break_types?.name || t('Break'),
      });
    }
  });

  detail.patrolRuns.forEach((run) => {
    const patrolName = run.patrols?.name || t('Patrol');

    if (run.started) {
      items.push({
        id: `patrol_start_${run.id}`,
        ts: new Date(run.started).getTime(),
        label: `${t('Patrol Started')} - ${patrolName}`,
        color: '#55a6ff',
        icon: 'patrol',
        category: 'patrol',
        meta: t('Patrol route'),
        groupId: run.id,
      });
    }

    (run.patrol_run_checkpoints || []).forEach((checkpoint) => {
      if (!checkpoint.completed_at) return;
      const checkpointName = getRunCheckpointName(checkpoint);
      items.push({
        id: `checkpoint_${checkpoint.id}`,
        ts: new Date(checkpoint.completed_at).getTime(),
        label: `${checkpointName} - ${t(checkpoint.status)}`,
        color: CHECKPOINT_STATUS_COLOR[checkpoint.status] || '#6b7280',
        icon: 'checkpoint',
        category: 'checkpoint',
        meta: patrolName,
        groupId: run.id,
      });
    });

    if (run.completed) {
      items.push({
        id: `patrol_end_${run.id}`,
        ts: new Date(run.completed).getTime(),
        label: `${t('Patrol Completed')} - ${patrolName}`,
        color: '#55a6ff',
        icon: 'patrol',
        category: 'patrol',
        meta: t('Patrol route'),
        groupId: run.id,
      });
    }
  });

  return items.sort((a, b) => a.ts - b.ts);
}

interface ShiftPlaybackMapProps {
  locationHistory: LocationPoint[];
  shiftDetail: { session: Partial<AttendanceSession> & { id?: string }; patrolRuns: PatrolRun[] } | TypedShiftDetail | null;
  timelineItems: TimelineItem[];
  jumpToTimeMs: number | null;
  onJumpConsumed: () => void;
  playbackLabel: string | null;
  onExitPatrolMode: (() => void) | null;
  fitKey: string | undefined;
}

function ShiftPlaybackMap({
  locationHistory,
  shiftDetail,
  timelineItems,
  jumpToTimeMs,
  onJumpConsumed,
  playbackLabel,
  onExitPatrolMode,
  fitKey,
}: ShiftPlaybackMapProps) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const announcedEventIdRef = useRef<string | null>(null);
  const previousTimeRef = useRef(0);
  const mapRef = useRef<MapRef | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackMs, setPlaybackMs] = useState(0);
  const [speed, setSpeed] = useState(10);
  const [hoverPct, setHoverPct] = useState<number | null>(null);
  const [selectedCheckpointId, setSelectedCheckpointId] = useState<string | null>(null);
  const [eventToast, setEventToast] = useState<EventToastState | null>(null);

  const session = shiftDetail?.session;
  const startMs = session?.check_in_at ? new Date(session.check_in_at).getTime() : 0;
  const fallbackEndMs = startMs + 8 * 3600 * 1000;
  const endMs = session?.check_out_at ? new Date(session.check_out_at).getTime() : fallbackEndMs;
  const totalDurationMs = Math.max(endMs - startMs, 1);
  const currentTimeMs = startMs + playbackMs;

  const currentPos = useMemo((): { lat: number; lng: number } | null => {
    if (!locationHistory.length) return null;
    const first = locationHistory[0];
    const last = locationHistory[locationHistory.length - 1];

    if (currentTimeMs <= first.recordedAt) return { lat: first.lat, lng: first.lng };
    if (currentTimeMs >= last.recordedAt) return { lat: last.lat, lng: last.lng };

    let index = 0;
    while (index < locationHistory.length - 1 && locationHistory[index + 1].recordedAt <= currentTimeMs) {
      index += 1;
    }

    const pointA = locationHistory[index];
    const pointB = locationHistory[index + 1];
    const ratio = (currentTimeMs - pointA.recordedAt) / (pointB.recordedAt - pointA.recordedAt);

    return {
      lat: pointA.lat + (pointB.lat - pointA.lat) * ratio,
      lng: pointA.lng + (pointB.lng - pointA.lng) * ratio,
    };
  }, [locationHistory, currentTimeMs]);

  const fullRoute = useMemo(
    () => locationHistory.map((point): [number, number] => [point.lat, point.lng]),
    [locationHistory],
  );

  const traversedRoute = useMemo(
    () => locationHistory.filter((point) => point.recordedAt <= currentTimeMs).map((point): [number, number] => [point.lat, point.lng]),
    [locationHistory, currentTimeMs],
  );

  const checkpoints = useMemo((): MappedCheckpoint[] => {
    const detail = shiftDetail as TypedShiftDetail | null;
    if (!detail?.patrolRuns) return [];

    const result: MappedCheckpoint[] = [];
    detail.patrolRuns.forEach((run) => {
      (run.patrol_run_checkpoints || []).forEach((checkpoint) => {
        const checkpointPoint = checkpoint.checkpoints;
        if (checkpointPoint?.latitude != null && checkpointPoint.longitude != null) {
          result.push({
            id: checkpoint.id,
            lat: checkpointPoint.latitude,
            lng: checkpointPoint.longitude,
            name: getRunCheckpointName(checkpoint),
            status: checkpoint.status,
            geofence: checkpointPoint.geofence_radius,
            completedAt: checkpoint.completed_at,
            patrolName: run.patrols?.name,
          });
        }
      });
    });

    return result;
  }, [shiftDetail]);

  const fullRouteFeature = useMemo(() => createLineFeature(fullRoute), [fullRoute]);
  const traversedRouteFeature = useMemo(() => createLineFeature(traversedRoute), [traversedRoute]);
  const checkpointGeofenceData = useMemo(() => createFeatureCollection(
    checkpoints.map((checkpoint) => (
      checkpoint.geofence
        ? createCircleFeature([checkpoint.lat, checkpoint.lng], checkpoint.geofence, 64, {
          color: CHECKPOINT_STATUS_COLOR[checkpoint.status] || '#6b7280',
        })
        : null
    )),
  ), [checkpoints]);
  const selectedCheckpoint = useMemo(
    () => checkpoints.find((checkpoint) => checkpoint.id === selectedCheckpointId) || null,
    [checkpoints, selectedCheckpointId],
  );

  const pct = (playbackMs / totalDurationMs) * 100;
  const hoverTimeMs = hoverPct != null ? startMs + (hoverPct / 100) * totalDurationMs : null;
  const nextTimelineItem = useMemo(
    () => timelineItems.find((item) => item.ts > currentTimeMs) || null,
    [timelineItems, currentTimeMs],
  );
  const markerItems = useMemo(
    () => timelineItems
      .filter((item) => item.ts >= startMs && item.ts <= endMs)
      .map((item) => ({
        ...item,
        left: ((item.ts - startMs) / totalDurationMs) * 100,
      })),
    [timelineItems, startMs, endMs, totalDurationMs],
  );

  const handleScrub = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPlaybackMs((parseFloat(event.target.value) / 100) * totalDurationMs);
    setIsPlaying(false);
  }, [totalDurationMs]);

  const handleScrubberMouseMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const pctHover = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    setHoverPct(pctHover * 100);
  }, []);

  const handlePlayPause = useCallback(() => {
    if (playbackMs >= totalDurationMs) setPlaybackMs(0);
    setIsPlaying((prev) => !prev);
  }, [playbackMs, totalDurationMs]);

  const handleJumpToTimeline = useCallback((ts: number) => {
    const offset = Math.max(0, Math.min(ts - startMs, totalDurationMs));
    setPlaybackMs(offset);
    setIsPlaying(false);
  }, [startMs, totalDurationMs]);

  useEffect(() => {
    setPlaybackMs(0);
    setIsPlaying(false);
    setEventToast(null);
    announcedEventIdRef.current = null;
    previousTimeRef.current = startMs;
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, [session?.id, startMs]);

  useEffect(() => {
    if (jumpToTimeMs == null) return;
    handleJumpToTimeline(jumpToTimeMs);
    onJumpConsumed();
  }, [jumpToTimeMs, handleJumpToTimeline, onJumpConsumed]);

  useEffect(() => {
    if (!isPlaying) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return undefined;
    }

    intervalRef.current = setInterval(() => {
      setPlaybackMs((prev) => {
        const next = prev + TICK_MS * speed;
        if (next >= totalDurationMs) {
          setIsPlaying(false);
          return totalDurationMs;
        }
        return next;
      });
    }, TICK_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, speed, totalDurationMs]);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  useEffect(() => {
    if (!timelineItems.length) {
      previousTimeRef.current = currentTimeMs;
      return;
    }

    if (currentTimeMs < previousTimeRef.current) {
      const latestReached = getLatestTimelineItemBefore(timelineItems, currentTimeMs);
      announcedEventIdRef.current = latestReached?.id ?? null;
      setEventToast(null);
    }

    previousTimeRef.current = currentTimeMs;
    const latestReached = getLatestTimelineItemBefore(timelineItems, currentTimeMs);
    if (!latestReached || latestReached.id === announcedEventIdRef.current) return;

    announcedEventIdRef.current = latestReached.id;
    setEventToast({
      item: latestReached,
      key: `${latestReached.id}-${latestReached.ts}-${currentTimeMs}`,
    });

    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setEventToast(null);
    }, 2200);
  }, [currentTimeMs, timelineItems]);

  useEffect(() => {
    setSelectedCheckpointId(null);
  }, [fitKey]);

  useEffect(() => {
    if (!mapRef.current || !locationHistory.length) return;

    if (locationHistory.length === 1) {
      mapRef.current.flyTo({
        center: [locationHistory[0].lng, locationHistory[0].lat],
        zoom: 16,
        duration: 900,
      });
      return;
    }

    const bounds = getBoundsFromPositions(locationHistory.map((point): [number, number] => [point.lat, point.lng]));
    if (bounds) {
      mapRef.current.fitBounds(bounds, { padding: 56, duration: 900 });
    }
  }, [fitKey, locationHistory]);

  if (!locationHistory.length) {
    return (
      <div className="playback-empty">
        <div className="playback-empty__icon">{t('No GPS data')}</div>
        <p className="playback-empty__text">{t('No location points were captured for this replay range.')}</p>
        <p className="playback-empty__sub">
          {t('The guard may have had location history disabled, or this patrol has no recorded track.')}
        </p>
      </div>
    );
  }

  const defaultViewState = {
    latitude: locationHistory[0].lat,
    longitude: locationHistory[0].lng,
    zoom: 15,
  };

  const sessionId = session?.id || 'default';
  const mapKey = fitKey || sessionId;

  return (
    <div className="playback">
      <div className="playback__map-wrap">
        <MapLibreMap
          ref={mapRef}
          initialViewState={defaultViewState}
          style={{ height: '100%', width: '100%' }}
          onClick={() => setSelectedCheckpointId(null)}
        >
          {fullRouteFeature && (
            <Source id={`shift-route-full-${mapKey}`} type="geojson" data={fullRouteFeature}>
              <Layer
                id={`shift-route-full-line-${mapKey}`}
                type="line"
                paint={{
                  'line-color': '#61708c',
                  'line-width': 3,
                  'line-opacity': 0.3,
                  'line-dasharray': [2, 2],
                }}
              />
            </Source>
          )}

          {traversedRouteFeature && (
            <Source id={`shift-route-traversed-${mapKey}`} type="geojson" data={traversedRouteFeature}>
              <Layer
                id={`shift-route-traversed-line-${mapKey}`}
                type="line"
                paint={{
                  'line-color': '#67b7ff',
                  'line-width': 4,
                  'line-opacity': 0.96,
                }}
              />
            </Source>
          )}

          {checkpointGeofenceData.features.length > 0 && (
            <Source id={`checkpoint-geofences-${mapKey}`} type="geojson" data={checkpointGeofenceData}>
              <Layer
                id={`checkpoint-geofences-fill-${mapKey}`}
                type="fill"
                paint={{
                  'fill-color': ['get', 'color'],
                  'fill-opacity': 0.08,
                }}
              />
              <Layer
                id={`checkpoint-geofences-outline-${mapKey}`}
                type="line"
                paint={{
                  'line-color': ['get', 'color'],
                  'line-width': 1.5,
                  'line-opacity': 0.34,
                }}
              />
            </Source>
          )}

          {currentPos && (
            <Marker latitude={currentPos.lat} longitude={currentPos.lng} anchor="center">
              <div className="playback-dot" />
            </Marker>
          )}

          {checkpoints.map((checkpoint) => (
            <MapDotMarker
              key={checkpoint.id}
              position={[checkpoint.lat, checkpoint.lng]}
              size={14}
              color={CHECKPOINT_STATUS_COLOR[checkpoint.status] || '#6b7280'}
              title={checkpoint.name || t('Checkpoint')}
              onClick={() => setSelectedCheckpointId(checkpoint.id)}
            />
          ))}

          {selectedCheckpoint && (
            <Popup
              latitude={selectedCheckpoint.lat}
              longitude={selectedCheckpoint.lng}
              anchor="top"
              offset={18}
              closeOnClick={false}
              onClose={() => setSelectedCheckpointId(null)}
            >
              <div className="map-popup__title">{selectedCheckpoint.name || t('Checkpoint')}</div>
              {selectedCheckpoint.patrolName && (
                <div className="map-popup__text">{selectedCheckpoint.patrolName}</div>
              )}
              <div className="map-popup__text">{t('Status: {status}', { status: t(selectedCheckpoint.status) })}</div>
              {selectedCheckpoint.completedAt && (
                <div className="map-popup__text">{t('Scanned: {time}', { time: fmt(selectedCheckpoint.completedAt, 'time') })}</div>
              )}
            </Popup>
          )}
        </MapLibreMap>

        <div className="playback__hud">
          <div className="playback__hud-left">
            {playbackLabel && (
              <div className="playback__mode-badge">
                {onExitPatrolMode && (
                  <button type="button" className="playback__mode-back" onClick={onExitPatrolMode}>
                    {t('View full shift')}
                  </button>
                )}
                <span className="playback__mode-label">{playbackLabel}</span>
              </div>
            )}
            {nextTimelineItem && (
              <div className="playback__next-event">
                <span className="playback__next-label">{t('Next')}</span>
                <span className="playback__next-value">{fmtMs(nextTimelineItem.ts)} - {nextTimelineItem.label}</span>
              </div>
            )}
          </div>

          <div className="playback__time-badge">{fmtMs(currentTimeMs)}</div>
        </div>

        {eventToast && (
          <div key={eventToast.key} className="playback__event-toast">
            <span
              className="playback__event-dot"
              style={{ background: eventToast.item.color }}
            />
            <div className="playback__event-copy">
              <span className="playback__event-label">{eventToast.item.label}</span>
              <span className="playback__event-meta">{eventToast.item.meta} - {fmtMs(eventToast.item.ts)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="playback__controls">
        <div className="playback__controls-left">
          <button
            type="button"
            className="playback__btn playback__btn--icon"
            onClick={() => {
              setPlaybackMs(0);
              setIsPlaying(false);
            }}
            title={t('Restart replay')}
          >
            {t('Restart')}
          </button>
          <button
            type="button"
            className={`playback__btn playback__btn--play ${isPlaying ? 'playback__btn--active' : ''}`}
            onClick={handlePlayPause}
            title={isPlaying ? t('Pause replay') : t('Play replay')}
          >
            {isPlaying ? t('Pause') : t('Play')}
          </button>
        </div>

        <div className="playback__scrubber-wrap">
          <span className="playback__scrubber-time">{fmt(startMs, 'time')}</span>
          <div
            className="playback__scrubber-track"
            onMouseMove={handleScrubberMouseMove}
            onMouseLeave={() => setHoverPct(null)}
          >
            <div className="playback__scrubber-progress" style={{ width: `${pct}%` }} />
            <div className="playback__scrubber-markers" aria-hidden="true">
              {markerItems.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className={`playback__marker playback__marker--${item.category}`}
                  style={{ left: `${item.left}%`, '--marker-color': item.color } as React.CSSProperties}
                  onClick={() => handleJumpToTimeline(item.ts)}
                  title={`${item.label} - ${fmtMs(item.ts)}`}
                />
              ))}
            </div>
            {hoverPct != null && (
              <div className="playback__scrubber-tooltip" style={{ left: `${hoverPct}%` }}>
                {fmtMs(hoverTimeMs)}
              </div>
            )}
            <input
              type="range"
              min={0}
              max={100}
              step={0.05}
              value={pct}
              onChange={handleScrub}
              className="playback__scrubber"
            />
          </div>
          <span className="playback__scrubber-time">{fmt(endMs, 'time')}</span>
        </div>

        <div className="playback__controls-right">
          <span className="playback__speed-label">{t('Speed')}</span>
          <div className="playback__speed-row">
            {SPEED_OPTIONS.map((value) => (
              <button
                type="button"
                key={value}
                className={`playback__speed-btn ${speed === value ? 'playback__speed-btn--active' : ''}`}
                onClick={() => setSpeed(value)}
              >
                {value}x
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface ShiftInspectorProps {
  detail: TypedShiftDetail;
  locationHistory: LocationPoint[];
  timelineItems: TimelineItem[];
  activePatrolRunId: string | null;
  activeTab: InspectorTab;
  onTabChange: (tab: InspectorTab) => void;
  timelineFilter: TimelineFilter;
  onTimelineFilterChange: (filter: TimelineFilter) => void;
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
  onJumpToTime: (ts: number) => void;
  onPlayPatrol: (patrolRun: PatrolRun | null) => void;
}

function ShiftInspector({
  detail,
  locationHistory,
  timelineItems,
  activePatrolRunId,
  activeTab,
  onTabChange,
  timelineFilter,
  onTimelineFilterChange,
  isCollapsed,
  onToggleCollapsed,
  onJumpToTime,
  onPlayPatrol,
}: ShiftInspectorProps) {
  const { language } = useI18n();
  const { session, breaks, patrolRuns, events } = detail;

  const totalBreakMinutes = useMemo(
    () => breaks.reduce((sum, entry) => sum + (entry.duration_actual_min || 0), 0),
    [breaks],
  );

  const totalCheckpoints = useMemo(
    () => patrolRuns.reduce((sum, run) => sum + (run.patrol_run_checkpoints?.filter(isRequiredRunCheckpoint).length || 0), 0),
    [patrolRuns],
  );

  const completedCheckpoints = useMemo(
    () => patrolRuns.reduce((sum, run) => {
      const completed = run.patrol_run_checkpoints
        ?.filter((checkpoint) => isRequiredRunCheckpoint(checkpoint) && checkpoint.status === 'completed')
        .length || 0;
      return sum + completed;
    }, 0),
    [patrolRuns],
  );

  const filteredTimelineItems = useMemo(
    () => (timelineFilter === 'all'
      ? timelineItems
      : timelineItems.filter((item) => item.category === timelineFilter)),
    [timelineItems, timelineFilter],
  );

  const recentTimelineItems = useMemo(
    () => [...timelineItems].sort((a, b) => b.ts - a.ts).slice(0, 5),
    [timelineItems],
  );

  const overviewFlags = useMemo(() => {
    const flags: string[] = [];
    if ((session.minutes_late ?? 0) > 0) flags.push(t('Late arrival by {count} minutes', { count: session.minutes_late ?? 0 }));
    if (!locationHistory.length) flags.push(t('No GPS history was captured for this shift'));
    if (!patrolRuns.length) flags.push(t('No patrol runs recorded'));
    if (!breaks.length) flags.push(t('No breaks recorded'));
    if (!flags.length) flags.push(t('No operational issues detected in the replay data'));
    return flags;
  }, [session.minutes_late, locationHistory.length, patrolRuns.length, breaks.length, language]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`shift-inspector ${isCollapsed ? 'shift-inspector--collapsed' : ''}`}>
      <div className="shift-inspector__bar">
        <div className="shift-inspector__tabs">
          {INSPECTOR_TABS.map((tab) => (
            <button
              key={tab.value}
              type="button"
              className={`shift-inspector__tab ${activeTab === tab.value ? 'shift-inspector__tab--active' : ''}`}
              onClick={() => onTabChange(tab.value)}
            >
              {t(tab.label)}
            </button>
          ))}
        </div>

        <div className="shift-inspector__actions">
          <span className="shift-inspector__hint">
            {activeTab === 'timeline'
              ? t('{count} activity markers', { count: filteredTimelineItems.length })
              : t('{count} total activity markers', { count: timelineItems.length })}
          </span>
          <button type="button" className="shift-inspector__toggle" onClick={onToggleCollapsed}>
            {isCollapsed ? t('Expand panel') : t('Collapse panel')}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="shift-inspector__body">
          {activeTab === 'overview' && (
            <div className="shift-detail">
              <div className="shift-detail__metric-grid">
                <div className="shift-detail__metric-card">
                  <span className="shift-detail__metric-label">{t('Attendance window')}</span>
                  <strong className="shift-detail__metric-value">
                    {fmt(session.check_in_at, 'time')} - {fmt(session.check_out_at, 'time')}
                  </strong>
                  <span className="shift-detail__metric-sub">{fmt(session.check_in_at, 'date')}</span>
                </div>
                <div className="shift-detail__metric-card">
                  <span className="shift-detail__metric-label">{t('Break coverage')}</span>
                  <strong className="shift-detail__metric-value">{breaks.length}</strong>
                  <span className="shift-detail__metric-sub">{t('{duration} logged', { duration: fmtDuration(totalBreakMinutes) })}</span>
                </div>
                <div className="shift-detail__metric-card">
                  <span className="shift-detail__metric-label">{t('Patrol checkpoints')}</span>
                  <strong className="shift-detail__metric-value">{completedCheckpoints}/{totalCheckpoints}</strong>
                  <span className="shift-detail__metric-sub">{t('{count} patrol routes', { count: patrolRuns.length })}</span>
                </div>
                <div className="shift-detail__metric-card">
                  <span className="shift-detail__metric-label">{t('Replay fidelity')}</span>
                  <strong className="shift-detail__metric-value">{locationHistory.length}</strong>
                  <span className="shift-detail__metric-sub">{t('GPS samples captured')}</span>
                </div>
              </div>

              <div className="shift-detail__section-grid">
                <section className="shift-detail__section-card">
                  <div className="shift-detail__section-head">
                    <h3 className="shift-detail__section-title">{t('Break Coverage')}</h3>
                    <span className="shift-detail__section-count">{breaks.length}</span>
                  </div>
                  {breaks.length ? breaks.map((entry) => (
                    <div key={entry.id} className="shift-detail__list-row">
                      <div>
                        <div className="shift-detail__list-title">{entry.break_types?.name || t('Break')}</div>
                        <div className="shift-detail__list-meta">
                          {fmt(entry.started_at, 'time')} - {fmt(entry.ended_at, 'time')}
                        </div>
                      </div>
                      <div className="shift-detail__list-side">
                        {entry.duration_actual_min != null ? `${entry.duration_actual_min}m` : '-'}
                        {entry.break_types?.is_paid && (
                          <span className="shift-detail__badge">{t('Paid')}</span>
                        )}
                      </div>
                    </div>
                  )) : (
                    <div className="shift-detail__empty">{t('No break entries were recorded for this shift.')}</div>
                  )}
                </section>

                <section className="shift-detail__section-card">
                  <div className="shift-detail__section-head">
                    <h3 className="shift-detail__section-title">{t('Operational Flags')}</h3>
                  </div>
                  <div className="shift-detail__flag-list">
                    {overviewFlags.map((flag) => (
                      <div key={flag} className="shift-detail__flag-item">{flag}</div>
                    ))}
                  </div>
                </section>
              </div>

              <section className="shift-detail__section-card">
                <div className="shift-detail__section-head">
                  <h3 className="shift-detail__section-title">{t('Recent Activity')}</h3>
                  <span className="shift-detail__section-count">{events.length + patrolRuns.length + breaks.length}</span>
                </div>
                {recentTimelineItems.length ? (
                  <div className="shift-detail__mini-timeline">
                    {recentTimelineItems.map((item) => (
                      <button
                        type="button"
                        key={item.id}
                        className="timeline-item"
                        onClick={() => onJumpToTime(item.ts)}
                        title={t('Jump to {time}', { time: fmtMs(item.ts) })}
                      >
                        <span className={`timeline-item__icon timeline-item__icon--${item.icon}`} />
                        <span className="timeline-item__dot" style={{ background: item.color }} />
                        <span className="timeline-item__body">
                          <span className="timeline-item__label">{item.label}</span>
                          <span className="timeline-item__time">{fmtMs(item.ts)} - {item.meta}</span>
                        </span>
                        <span className="timeline-item__arrow">{t('Jump')}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="shift-detail__empty">{t('No activity markers are available for this replay.')}</div>
                )}
              </section>
            </div>
          )}

          {activeTab === 'patrols' && (
            <div className="shift-detail">
              <section className="shift-detail__section-card">
                <div className="shift-detail__section-head">
                  <h3 className="shift-detail__section-title">{t('Patrol Routes')}</h3>
                  <span className="shift-detail__section-count">{patrolRuns.length}</span>
                </div>
                {patrolRuns.length ? patrolRuns.map((run) => {
                  const requiredCheckpoints = run.patrol_run_checkpoints?.filter(isRequiredRunCheckpoint) || [];
                  const routeOnly = (run.patrol_run_checkpoints?.length || 0) - requiredCheckpoints.length;
                  const total = requiredCheckpoints.length;
                  const done = requiredCheckpoints.filter((checkpoint) => checkpoint.status === 'completed').length;
                  const skipped = requiredCheckpoints.filter((checkpoint) => checkpoint.status === 'skipped').length;
                  const isActive = activePatrolRunId === run.id;

                  return (
                    <div
                      key={run.id}
                      className={`shift-detail__patrol-row ${isActive ? 'shift-detail__patrol-row--active' : ''}`}
                    >
                      <div className="shift-detail__patrol-main">
                        <div className="shift-detail__patrol-name">{run.patrols?.name || t('Patrol')}</div>
                        <div className="shift-detail__patrol-time">
                          {fmt(run.started, 'time')} - {fmt(run.completed, 'time')}
                        </div>
                      </div>
                      <div className="shift-detail__patrol-progress">
                        <strong>{done}/{total}</strong>
                        {skipped > 0 && <span>{t('{count} skipped', { count: skipped })}</span>}
                        {routeOnly > 0 && <span>{t('{count} route-only', { count: routeOnly })}</span>}
                      </div>
                      <button
                        type="button"
                        className={`shift-detail__patrol-play ${isActive ? 'shift-detail__patrol-play--active' : ''}`}
                        onClick={() => (isActive ? onPlayPatrol(null) : onPlayPatrol(run))}
                      >
                        {isActive ? t('Viewing') : t('Replay')}
                      </button>
                    </div>
                  );
                }) : (
                  <div className="shift-detail__empty">{t('No patrol runs were attached to this shift.')}</div>
                )}
              </section>
            </div>
          )}

          {activeTab === 'timeline' && (
            <div className="shift-detail">
              <div className="shift-detail__filter-row">
                {TIMELINE_FILTERS.map((filter) => (
                  <button
                    key={filter.value}
                    type="button"
                    className={`shift-detail__filter-chip ${timelineFilter === filter.value ? 'shift-detail__filter-chip--active' : ''}`}
                    onClick={() => onTimelineFilterChange(filter.value)}
                  >
                    {t(filter.label)}
                  </button>
                ))}
              </div>

              <div className="shift-detail__timeline">
                {filteredTimelineItems.length ? filteredTimelineItems.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    className="timeline-item"
                    onClick={() => onJumpToTime(item.ts)}
                    title={t('Jump to {time}', { time: fmtMs(item.ts) })}
                  >
                    <span className={`timeline-item__icon timeline-item__icon--${item.icon}`} />
                    <span className="timeline-item__dot" style={{ background: item.color }} />
                    <span className="timeline-item__body">
                      <span className="timeline-item__label">{item.label}</span>
                      <span className="timeline-item__time">{fmtMs(item.ts)} - {item.meta}</span>
                    </span>
                    <span className="timeline-item__arrow">{t('Jump')}</span>
                  </button>
                )) : (
                  <div className="shift-detail__empty">{t('No activity matches the selected timeline filter.')}</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ShiftHistoryPage({
  navigationTarget = null,
  onNavigationHandled,
}: Props) {
  const { language } = useI18n();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [shifts, setShifts] = useState<AttendanceSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedShift, setSelectedShift] = useState<AttendanceSession | null>(null);
  const [shiftDetail, setShiftDetail] = useState<TypedShiftDetail | null>(null);
  const [locationHistory, setLocationHistory] = useState<LocationPoint[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [siteFilter, setSiteFilter] = useState<string>('all');
  const [rangeFilter, setRangeFilter] = useState<ShiftRange>('all');
  const [sortBy, setSortBy] = useState<ShiftSort>('latest');
  const [jumpToTimeMs, setJumpToTimeMs] = useState<number | null>(null);
  const [activePatrol, setActivePatrol] = useState<ActivePatrol | null>(null);
  const [patrolLoading, setPatrolLoading] = useState(false);
  const [inspectorTab, setInspectorTab] = useState<InspectorTab>('overview');
  const [timelineFilter, setTimelineFilter] = useState<TimelineFilter>('all');
  const [isInspectorCollapsed, setIsInspectorCollapsed] = useState(false);
  const handledNavigationNonceRef = useRef<number | null>(null);

  useEffect(() => {
    fetchCompanyId().then((id) => setCompanyId(id));
  }, []);

  useEffect(() => {
    if (!companyId) return;

    setLoading(true);
    fetchCompletedShifts(companyId)
      .then((data) => setShifts((prev) => mergeAttendanceSessions(data, prev)))
      .catch((error) => console.error('Shift history fetch error:', error))
      .finally(() => setLoading(false));
  }, [companyId]);

  const handleSelectShift = useCallback(async (shift: AttendanceSession) => {
    setSelectedShift(shift);
    setShiftDetail(null);
    setLocationHistory([]);
    setJumpToTimeMs(null);
    setActivePatrol(null);
    setInspectorTab('overview');
    setTimelineFilter('all');
    setIsInspectorCollapsed(false);
    setDetailLoading(true);

    try {
      const [detail, locations] = await Promise.all([
        fetchShiftDetail(shift.id),
        shift.check_in_at
          ? fetchShiftLocationHistory(shift.id, shift.guard_id, shift.check_in_at, shift.check_out_at)
          : Promise.resolve([]),
      ]);

      const typedDetail = detail as TypedShiftDetail;
      setShiftDetail(typedDetail);

      let replayLocations = locations;
      if (!replayLocations.length && typedDetail.patrolRuns.length) {
        const patrolRunIds = typedDetail.patrolRuns.map((run) => run.id);
        replayLocations = await fetchPatrolRunLocations(patrolRunIds);
      }

      setLocationHistory(replayLocations);
    } catch (error) {
      console.error('Error loading shift detail:', error);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!navigationTarget?.shiftExecutionId) return;
    if (handledNavigationNonceRef.current === navigationTarget.nonce) return;

    handledNavigationNonceRef.current = navigationTarget.nonce;

    let isCancelled = false;

    const openLinkedShift = async () => {
      try {
        const existingShift = shifts.find((shift) => shift.id === navigationTarget.shiftExecutionId) ?? null;
        const targetShift = existingShift ?? await fetchShiftSessionById(navigationTarget.shiftExecutionId);

        if (isCancelled) return;

        if (!existingShift) {
          setShifts((prev) => mergeAttendanceSessions([targetShift], prev));
        }

        await handleSelectShift(targetShift);
      } catch (error) {
        console.error('Error opening linked shift replay:', error);
      } finally {
        if (!isCancelled) {
          onNavigationHandled?.();
        }
      }
    };

    openLinkedShift();

    return () => {
      isCancelled = true;
    };
  }, [handleSelectShift, navigationTarget, onNavigationHandled, shifts]);

  const handlePlayPatrol = useCallback(async (patrolRun: PatrolRun | null) => {
    if (!patrolRun) {
      setActivePatrol(null);
      setJumpToTimeMs(null);
      return;
    }

    setPatrolLoading(true);
    try {
      const locations = await fetchPatrolRunLocations([patrolRun.id]);
      setActivePatrol({ patrolRun, locations });
      setJumpToTimeMs(null);
      setInspectorTab('patrols');
    } catch (error) {
      console.error('Error loading patrol locations:', error);
    } finally {
      setPatrolLoading(false);
    }
  }, []);

  const statusOptions = useMemo(
    () => [...new Set(shifts.map((shift) => shift.status).filter(Boolean) as string[])].sort(),
    [shifts],
  );

  const siteOptions = useMemo(
    () => [...new Set(shifts.map((shift) => getSiteName(shift)).filter((name) => name && name !== '-'))].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [shifts],
  );

  const filteredShifts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return [...shifts]
      .filter((shift) => {
        const guardName = getGuardName(shift).toLowerCase();
        const siteName = getSiteName(shift).toLowerCase();
        if (!term) return true;
        return guardName.includes(term) || siteName.includes(term);
      })
      .filter((shift) => (statusFilter === 'all' ? true : shift.status === statusFilter))
      .filter((shift) => (siteFilter === 'all' ? true : getSiteName(shift) === siteFilter))
      .filter((shift) => matchesRange(shift.check_in_at, rangeFilter))
      .sort((a, b) => compareShifts(a, b, sortBy));
  }, [shifts, searchTerm, statusFilter, siteFilter, rangeFilter, sortBy]);

  const activeFilterCount = [
    searchTerm.trim() ? 1 : 0,
    statusFilter !== 'all' ? 1 : 0,
    siteFilter !== 'all' ? 1 : 0,
    rangeFilter !== 'all' ? 1 : 0,
    sortBy !== 'latest' ? 1 : 0,
  ].reduce((sum, value) => sum + value, 0);

  const timelineItems = useMemo(
    () => (shiftDetail ? buildTimelineItems(shiftDetail) : []),
    [shiftDetail, language], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const mapLocationHistory = activePatrol ? activePatrol.locations : locationHistory;
  const mapShiftDetail: ShiftPlaybackMapProps['shiftDetail'] = activePatrol
    ? {
      session: {
        ...shiftDetail?.session,
        check_in_at: activePatrol.patrolRun.started ?? null,
        check_out_at: activePatrol.patrolRun.completed ?? null,
        id: `patrol_${activePatrol.patrolRun.id}`,
      },
      patrolRuns: [activePatrol.patrolRun],
    }
    : shiftDetail;

  const mapFitKey = activePatrol
    ? `patrol_${activePatrol.patrolRun.id}`
    : shiftDetail?.session?.id;

  const mapLabel = activePatrol
    ? `${t('Patrol focus')} - ${activePatrol.patrolRun.patrols?.name || t('Patrol')}`
    : null;

  const mapTimelineItems = useMemo(() => {
    if (!activePatrol) return timelineItems;
    return timelineItems.filter((item) => item.groupId === activePatrol.patrolRun.id);
  }, [timelineItems, activePatrol]);

  const replayContext = useMemo((): ReplayContext | null => {
    if (!selectedShift) return null;

    const session = shiftDetail?.session || selectedShift;
    const startTime = activePatrol?.patrolRun.started || session.check_in_at;
    const endTime = activePatrol?.patrolRun.completed || session.check_out_at;
    const durationMinutes = getWindowMinutes(startTime, endTime) ?? getShiftDurationMinutes(session);

    return {
      mode: activePatrol ? t('Patrol Replay') : t('Shift Replay'),
      guardName: getGuardName(session),
      siteName: getSiteName(session),
      date: fmt(selectedShift.check_in_at, 'date'),
      start: fmt(startTime, 'time'),
      end: fmt(endTime, 'time'),
      duration: fmtDuration(durationMinutes),
      status: session.status ?? null,
      patrolName: activePatrol?.patrolRun.patrols?.name ?? null,
      gpsPoints: mapLocationHistory.length,
      events: shiftDetail?.events.length || 0,
      patrolRuns: shiftDetail?.patrolRuns.length || 0,
      currentPatrolCp: activePatrol?.patrolRun.patrol_run_checkpoints?.length || 0,
    };
  }, [selectedShift, shiftDetail, activePatrol, mapLocationHistory.length, language]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setStatusFilter('all');
    setSiteFilter('all');
    setRangeFilter('all');
    setSortBy('latest');
  }, []);

  return (
    <div className="shift-history">
      <aside className="shift-history__library">
        <div className="shift-history__library-head">
          <div>
            <p className="shift-history__eyebrow">{t('Shift Intelligence')}</p>
            <h2 className="shift-history__title">{t('Replay Library')}</h2>
            <p className="shift-history__subtitle">
              {t('Find completed shifts fast, then inspect routes, patrol coverage and attendance activity without losing map space.')}
            </p>
          </div>

          <div className="shift-history__library-stats">
            <div className="shift-history__stat-chip">
              <span>{t('Total')}</span>
              <strong>{shifts.length}</strong>
            </div>
            <div className="shift-history__stat-chip">
              <span>{t('Visible')}</span>
              <strong>{filteredShifts.length}</strong>
            </div>
          </div>
        </div>

        <div className="shift-history__library-toolbar">
          <input
            type="text"
            className="shift-history__search"
            placeholder={t('Search by guard or site')}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />

          <div className="shift-history__filter-grid">
            <label className="shift-history__field">
              <span className="shift-history__field-label">{t('Status')}</span>
              <select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="shift-history__select"
              >
                <option value="all">{t('All statuses')}</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>{t(STATUS_LABEL[status] || status.replace(/_/g, ' '))}</option>
                ))}
              </select>
            </label>

            <label className="shift-history__field">
              <span className="shift-history__field-label">{t('Site')}</span>
              <select
                value={siteFilter}
                onChange={(event) => setSiteFilter(event.target.value)}
                className="shift-history__select"
              >
                <option value="all">{t('All sites')}</option>
                {siteOptions.map((site) => (
                  <option key={site} value={site}>{site}</option>
                ))}
              </select>
            </label>

            <label className="shift-history__field">
              <span className="shift-history__field-label">{t('Range')}</span>
              <select
                value={rangeFilter}
                onChange={(event) => setRangeFilter(event.target.value as ShiftRange)}
                className="shift-history__select"
              >
                {SHIFT_RANGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{t(option.label)}</option>
                ))}
              </select>
            </label>

            <label className="shift-history__field">
              <span className="shift-history__field-label">{t('Sort')}</span>
              <select
                value={sortBy}
                onChange={(event) => setSortBy(event.target.value as ShiftSort)}
                className="shift-history__select"
              >
                {SHIFT_SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{t(option.label)}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="shift-history__toolbar-meta">
            <span className="shift-history__results-copy">
              {tn('{count} result', '{count} results', filteredShifts.length)}
              {activeFilterCount > 0 ? ` - ${tn('{count} filter active', '{count} filters active', activeFilterCount)}` : ''}
            </span>
            {activeFilterCount > 0 && (
              <button type="button" className="shift-history__clear-btn" onClick={clearFilters}>
                {t('Clear filters')}
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner spinner--sm" /></div>
        ) : filteredShifts.length === 0 ? (
          <div className="shift-history__empty-list">{t('No shifts match the current replay filters.')}</div>
        ) : (
          <div className="shift-history__list-scroll">
            {filteredShifts.map((shift) => {
              const isSelected = selectedShift?.id === shift.id;
              const guardName = getGuardName(shift);
              const siteName = getSiteName(shift);
              const duration = fmtDuration(getShiftDurationMinutes(shift));

              return (
                <button
                  type="button"
                  key={shift.id}
                  className={`shift-card ${isSelected ? 'shift-card--selected' : ''}`}
                  onClick={() => handleSelectShift(shift)}
                >
                  <div className="shift-card__header">
                    <div className="shift-card__identity">
                      <span
                        className="shift-card__avatar"
                        style={getAvatarAccentStyle(`${shift.guard_id ?? guardName}-${shift.id}`)}
                      >
                        {getInitials(guardName)}
                      </span>
                      <div className="shift-card__names">
                        <span className="shift-card__guard">{guardName}</span>
                        <span className="shift-card__site">{siteName}</span>
                      </div>
                    </div>

                    <span
                      className="shift-card__status"
                      style={{ background: STATUS_COLOR[shift.status ?? ''] || '#6b7280' }}
                    >
                      {shift.status ? t(STATUS_LABEL[shift.status] || shift.status.replace(/_/g, ' ')) : '-'}
                    </span>
                  </div>

                  <div className="shift-card__meta-grid">
                    <div className="shift-card__meta-item">
                      <span className="shift-card__meta-label">{t('Date')}</span>
                      <span className="shift-card__meta-value">{fmt(shift.check_in_at, 'date')}</span>
                    </div>
                    <div className="shift-card__meta-item">
                      <span className="shift-card__meta-label">{t('Window')}</span>
                      <span className="shift-card__meta-value">
                        {fmt(shift.check_in_at, 'time')} - {shift.check_out_at ? fmt(shift.check_out_at, 'time') : t('Open||shift window')}
                      </span>
                    </div>
                    <div className="shift-card__meta-item">
                      <span className="shift-card__meta-label">{t('Duration')}</span>
                      <span className="shift-card__meta-value">{duration}</span>
                    </div>
                    <div className="shift-card__meta-item">
                      <span className="shift-card__meta-label">{t('Late')}</span>
                      <span className="shift-card__meta-value">{shift.minutes_late ? `${shift.minutes_late}m` : t('On time')}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </aside>

      <section className="shift-history__workspace">
        {!selectedShift ? (
          <div className="shift-history__placeholder-card">
            <div className="shift-history__placeholder-icon">{t('Replay')}</div>
            <h3 className="shift-history__placeholder-title">{t('Select a completed shift')}</h3>
            <p className="shift-history__placeholder-copy">
              {t('The replay workspace is now optimized for scale. Pick a shift from the library to inspect route playback, patrol checkpoints and activity markers.')}
            </p>
          </div>
        ) : (
          <>
            {replayContext && (
              <div className="shift-history__summary-card">
                <div className="shift-history__summary-head">
                  <div className="shift-history__summary-identity">
                    <div
                      className="shift-history__summary-avatar"
                      style={getAvatarAccentStyle(replayContext.guardName)}
                    >
                      {getInitials(replayContext.guardName)}
                    </div>
                    <div className="shift-history__summary-copy">
                      <span className="shift-history__summary-kicker">{t('Selected Replay')}</span>
                      <h3 className="shift-history__summary-title">{replayContext.guardName}</h3>
                      <div className="shift-history__summary-meta">
                        <span>{replayContext.siteName}</span>
                        <span>{replayContext.date}</span>
                        <span>{replayContext.mode}</span>
                        {replayContext.patrolName && <span>{replayContext.patrolName}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="shift-history__summary-actions">
                    {replayContext.status && (
                      <span
                        className="shift-history__summary-status"
                        style={{ '--status-color': STATUS_COLOR[replayContext.status] || '#6b7280' } as React.CSSProperties}
                      >
                        {t(STATUS_LABEL[replayContext.status] || replayContext.status.replace(/_/g, ' '))}
                      </span>
                    )}
                    {activePatrol && (
                      <button type="button" className="shift-history__summary-btn" onClick={() => handlePlayPatrol(null)}>
                        {t('Exit patrol focus')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="shift-history__summary-metrics">
                  <div className="shift-history__summary-metric">
                    <span className="shift-history__summary-label">{t('Window')}</span>
                    <strong className="shift-history__summary-value">{replayContext.start} - {replayContext.end}</strong>
                  </div>
                  <div className="shift-history__summary-metric">
                    <span className="shift-history__summary-label">{t('Duration')}</span>
                    <strong className="shift-history__summary-value">{replayContext.duration}</strong>
                  </div>
                  <div className="shift-history__summary-metric">
                    <span className="shift-history__summary-label">{t('GPS trail')}</span>
                    <strong className="shift-history__summary-value">{replayContext.gpsPoints}</strong>
                  </div>
                  <div className="shift-history__summary-metric">
                    <span className="shift-history__summary-label">{t('Events')}</span>
                    <strong className="shift-history__summary-value">{replayContext.events}</strong>
                  </div>
                  <div className="shift-history__summary-metric">
                    <span className="shift-history__summary-label">{activePatrol ? t('Checkpoints') : t('Patrols')}</span>
                    <strong className="shift-history__summary-value">
                      {activePatrol ? replayContext.currentPatrolCp : replayContext.patrolRuns}
                    </strong>
                  </div>
                </div>
              </div>
            )}

            <div className="shift-history__map-card">
              <div className="shift-history__map-header">
                <div>
                  <p className="shift-history__eyebrow">{t('Replay Workspace')}</p>
                  <h3 className="shift-history__map-title">
                    {activePatrol
                      ? t('Patrol focus: {name}', { name: activePatrol.patrolRun.patrols?.name || t('Patrol') })
                      : t('Shift route playback')}
                  </h3>
                  <p className="shift-history__map-subtitle">
                    {t('Playback is bounded to a dedicated stage. Event markers pulse on the route and timeline markers are clickable from the scrubber.')}
                  </p>
                </div>

                <div className="shift-history__map-chips">
                  <span className="shift-history__map-chip">{t('Markers {count}', { count: mapTimelineItems.length })}</span>
                  <span className="shift-history__map-chip">{t('Points {count}', { count: mapLocationHistory.length })}</span>
                  {activePatrol && <span className="shift-history__map-chip">{t('Patrol focus')}</span>}
                </div>
              </div>

              <div className="shift-history__map-stage">
                {(detailLoading || patrolLoading) ? (
                  <div className="loading"><div className="spinner spinner--sm" /></div>
                ) : shiftDetail ? (
                  <ShiftPlaybackMap
                    locationHistory={mapLocationHistory}
                    shiftDetail={mapShiftDetail}
                    timelineItems={mapTimelineItems}
                    jumpToTimeMs={jumpToTimeMs}
                    onJumpConsumed={() => setJumpToTimeMs(null)}
                    playbackLabel={mapLabel}
                    onExitPatrolMode={activePatrol ? () => handlePlayPatrol(null) : null}
                    fitKey={mapFitKey}
                  />
                ) : (
                  <div className="shift-history__placeholder-card shift-history__placeholder-card--inline">
                    <div className="shift-history__placeholder-icon">{t('Map')}</div>
                    <h3 className="shift-history__placeholder-title">{t('Replay data is unavailable')}</h3>
                    <p className="shift-history__placeholder-copy">
                      {t('The selected shift could not be loaded. Try another replay from the library.')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {shiftDetail && (
              <ShiftInspector
                detail={shiftDetail}
                locationHistory={locationHistory}
                timelineItems={timelineItems}
                activePatrolRunId={activePatrol?.patrolRun.id ?? null}
                activeTab={inspectorTab}
                onTabChange={setInspectorTab}
                timelineFilter={timelineFilter}
                onTimelineFilterChange={setTimelineFilter}
                isCollapsed={isInspectorCollapsed}
                onToggleCollapsed={() => setIsInspectorCollapsed((prev) => !prev)}
                onJumpToTime={setJumpToTimeMs}
                onPlayPatrol={handlePlayPatrol}
              />
            )}
          </>
        )}
      </section>
    </div>
  );
}
